import { logger, TimeAgo, Tooltip } from '@/ui'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import DOMPurify from 'dompurify'
import {
	CheckIcon,
	CornerUpLeftIcon,
	DownloadIcon,
	EyeIcon,
	FileIcon,
	LinkIcon,
	MailIcon,
	MailOpenIcon,
	MoreVerticalIcon,
	PaperclipIcon,
} from 'lucide-react'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { canDownloadScannedFile, getScanPresentation, isScanBlocked, isScanPending } from '../../../shared/scan-status'
import type { Email, EmailAttachment } from './types'

function formatFileSize(bytes: number): string {
	if (bytes < 1024) { return `${bytes} B` }
	if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB` }
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AttachmentChip({ attachment }: { attachment: EmailAttachment }) {
	const { t } = useTranslation('common')
	const scan = getScanPresentation(attachment.scanStatus)
	const ScanIcon = scan.Icon
	const blocked = isScanBlocked(attachment.scanStatus)
	const downloadable = canDownloadScannedFile(attachment.scanStatus) && Boolean(attachment.url)

	if (downloadable && attachment.url) {
		return (
			<a
				href={attachment.url}
				target="_blank"
				rel="noopener noreferrer"
				onClick={(e) => e.stopPropagation()}
				className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors group"
			>
				<FileIcon className="size-4 text-gray-400" />
				<div className="flex flex-col min-w-0">
					<span className="text-sm text-gray-700 truncate max-w-[200px]">{attachment.name}</span>
					<span className="text-xs text-gray-400">{formatFileSize(attachment.size)}</span>
				</div>
				<DownloadIcon className="size-4 text-gray-400 group-hover:text-gray-600" />
			</a>
		)
	}

	const title = blocked
		? (attachment.scanSignature
			? `${t('fileScan.blocked')} — ${t('fileScan.signature', { signature: attachment.scanSignature })}`
			: t('fileScan.blocked'))
		: t('fileScan.pendingHint')

	return (
		<div
			title={title}
			className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${
				blocked ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
			}`}
		>
			<ScanIcon className={`size-4 flex-shrink-0 ${scan.tagClass} ${scan.spin ? 'animate-spin' : ''}`} />
			<div className="flex flex-col min-w-0">
				<span className="text-sm text-gray-700 truncate max-w-[200px]">{attachment.name}</span>
				<span className={`text-xs font-medium ${scan.tagClass}`}>{t(scan.labelKey)}</span>
			</div>
		</div>
	)
}

export interface EmailItemProps {
	email: Email
	isHighlighted?: boolean
	isBeingRepliedTo?: boolean
	onReply: (emailId: string) => void
	onToggleRead: (emailId: string) => void
	onClick?: (emailId: string) => void
	emailUrl?: string
}

export const EmailItem = memo(function EmailItem({
	email,
	isHighlighted,
	isBeingRepliedTo,
	onReply,
	onToggleRead,
	onClick,
	emailUrl,
}: EmailItemProps) {
	const { t } = useTranslation('common')
	const [copied, setCopied] = useState(false)

	const handleCopyLink = useCallback(async () => {
		if (!emailUrl) { return }

		try {
			await navigator.clipboard.writeText(emailUrl)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (err) {
			logger.error(err, {
				tags: { component: 'EmailItem', action: 'copyLink' },
			})
		}
	}, [emailUrl])

	const handleClick = useCallback(() => {
		onClick?.(email.id)
	}, [onClick, email.id])

	const handleReply = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		onReply(email.id)
	}, [onReply, email.id])

	const handleToggleRead = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		onToggleRead(email.id)
	}, [onToggleRead, email.id])

	const isOutbound = !email.isFromCandidate

	const containerClassName = useMemo(
		() =>
			ctl(`
		py-4 px-5 cursor-pointer transition-colors
		${isHighlighted ? 'bg-blue-50' : ''}
		${isBeingRepliedTo ? 'bg-purple-50' : ''}
	`),
		[isHighlighted, isBeingRepliedTo],
	)

	const bubbleClassName = useMemo(
		() =>
			ctl(`
		rounded-2xl px-4 py-3 max-w-[85%]
		${
				isOutbound
					? 'bg-brand-600 text-white ml-auto rounded-br-md'
					: 'bg-gray-100 text-gray-900 rounded-bl-md'
			}
	`),
		[isOutbound],
	)

	return (
		<div id={`email-${email.id}`} className={containerClassName} onClick={handleClick}>
			{/* Replying indicator */}
			{isBeingRepliedTo && (
				<div className="mb-3 px-3 py-1.5 bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1.5 rounded-full w-fit">
					<CornerUpLeftIcon className="size-3" />
					{t('emailItem.replyingTo')}
				</div>
			)}

			{/* Header row with avatar, name, and timestamp */}
			<div className={`flex items-center gap-2 mb-2 ${isOutbound ? 'justify-end' : ''}`}>
				{/* Inbound: Avatar + Name on left, timestamp on right */}
				{!isOutbound && (
					<>
						<div className="size-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold bg-gray-200 text-gray-600">
							{email.from.name.charAt(0).toUpperCase()}
						</div>
						<span className="ph-no-capture text-sm font-medium text-gray-900">
							{email.from.name}
						</span>
						{!email.isRead && <span className="size-2 rounded-full bg-brand-500 flex-shrink-0" />}
						<span className="text-xs text-gray-400 ml-auto">
							<TimeAgo date={email.sentAt} short />
						</span>
					</>
				)}
				{/* Outbound: Timestamp on left/center, Name + Avatar on right */}
				{isOutbound && (
					<>
						<span className="text-xs text-gray-400">
							<TimeAgo date={email.sentAt} short />
						</span>
						<span className="ph-no-capture text-sm font-medium text-gray-900 ml-auto">
							{email.from.name}
						</span>
						<div className="size-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold bg-brand-100 text-brand-700">
							{email.from.name.charAt(0).toUpperCase()}
						</div>
					</>
				)}
			</div>

			{/* Message bubble row */}
			<div className={`flex ${isOutbound ? 'justify-end' : 'pl-10'}`}>
				{/* Message content */}
				<div className={`flex flex-col gap-1 ${isOutbound ? 'items-end' : 'items-start'}`}>
					{/* Message bubble */}
					<div className={bubbleClassName}>
						{/* Subject if present */}
						{email.subject && (
							<div
								className={`text-xs font-medium mb-1 ${isOutbound ? 'text-white/70' : 'text-gray-500'}`}
							>
								{email.subject}
							</div>
						)}
						{/* Email body */}
						<div
							className={ctl(`
								text-sm leading-relaxed
								[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1
								[&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5
								${
								isOutbound ? '[&_a]:text-white [&_a]:underline' : '[&_a]:text-brand-600 [&_a]:underline'
							}
							`)}
							dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body) }}
						/>
					</div>

					{/* Attachments */}
					{email.attachments.length > 0 && (
						<div className="mt-2">
							<div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
								<PaperclipIcon className="size-3.5" />
								{t('emailItem.attachment', { count: email.attachments.length })}
							</div>
							<div className="flex flex-wrap gap-2">
								{email.attachments.map((attachment) => (
									<AttachmentChip key={attachment.id} attachment={attachment} />
								))}
							</div>
						</div>
					)}

					{/* Actions */}
					<div className={`flex items-center gap-1 mt-1 ${isOutbound ? 'flex-row-reverse' : ''}`}>
						<button
							type="button"
							onClick={handleReply}
							className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
						>
							<CornerUpLeftIcon className="size-3.5" />
							{t('emailItem.reply')}
						</button>

						{/* Read by indicator - only show for inbound emails */}
						{email.isFromCandidate && email.readBy.length > 0 && (
							<Tooltip
								content={t('emailItem.seenBy', { count: email.readBy.length })}
							>
								<div className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400">
									<EyeIcon className="size-3" />
									<span>{email.readBy.length}</span>
								</div>
							</Tooltip>
						)}

						{/* More actions menu */}
						<Menu as="div" className="relative">
							<MenuButton className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
								<MoreVerticalIcon className="size-3.5" />
							</MenuButton>
							<MenuItems className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg ring-1 ring-black/5 py-1 z-50">
								{emailUrl && (
									<MenuItem>
										<button
											type="button"
											onClick={handleCopyLink}
											className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
										>
											{copied
												? (
													<>
														<CheckIcon className="size-4 text-green-500" />
														{t('emailItem.copied')}
													</>
												)
												: (
													<>
														<LinkIcon className="size-4" />
														{t('emailItem.copyLink')}
													</>
												)}
										</button>
									</MenuItem>
								)}
								<MenuItem>
									<button
										type="button"
										onClick={handleToggleRead}
										className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
									>
										{email.isRead
											? (
												<>
													<MailIcon className="size-4" />
													{t('emailItem.markUnread')}
												</>
											)
											: (
												<>
													<MailOpenIcon className="size-4" />
													{t('emailItem.markRead')}
												</>
											)}
									</button>
								</MenuItem>
							</MenuItems>
						</Menu>
					</div>
				</div>
			</div>
		</div>
	)
})
